import {
  SetupTasksParams,
  TASKS_ANALYTICS_LABEL,
  TASK_EVENT_FEATURE_TYPE,
  TASK_EVENT_ACTIONS,
  CustomSubscription,
} from '@nx-frontend/features/web-editor/types';
import { WindowCommon, TaskEventSubject } from '@nx-frontend/features/web-editor/types';
import { onWindowCommonReady } from '@nx-frontend/features/web-editor/util';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { EditorInstance, TranslatesFnType } from '@nx-frontend/shared/types';
import { randomString } from '@nx-frontend/shared/utils';
import { TasksApiProxyService } from './tasks-api-proxy';

export class TasksManager {
  public enabled = true;

  private TasksApiProxy: TasksApiProxyService;
  private windowCommonSubscription$: CustomSubscription | null = null;

  constructor(
    private workspaceId: string,
    private noteId: string,
    private isFree: boolean,
    private orgId: string,
    private readOnly: boolean,
    private editorMode: string,
    private canInvite: boolean,
    private isWorkspaceMember: boolean,
    private theme: string,
    private tasksAllowedActions: object | undefined,
    private gatewayUrl: string,
    private sessionId: string,
    private boxUrl: string,
    private portalId: string | null,
    public waitCustomElements: () => void,
    public onCustomElementsLoaded: () => void,
    private editorInstanceGetter: () => EditorInstance | null,
    private applyMobileStyles: boolean,
    private t?: TranslatesFnType
  ) {
    this.TasksApiProxy = new TasksApiProxyService(gatewayUrl, sessionId);

    onWindowCommonReady((windowCommon: WindowCommon) => {
      this.listenTasksEvents(windowCommon.taskEventsSubj$);
    });
  }

  public destroy() {
    this.windowCommonSubscription$?.unsubscribe();
  }

  public listenTasksEvents = (taskEventsSubj$: TaskEventSubject) => {
    this.windowCommonSubscription$ = taskEventsSubj$.subscribe((event) => {
      if (event.featureType === TASK_EVENT_FEATURE_TYPE.TASK_LIST) {
        const { tasksManager: myPatchedInstance } = this.editorInstanceGetter()?.plugins || {};
        switch (event.eventType) {
          case TASK_EVENT_ACTIONS.TASK_LIST_DELETE:
            if (typeof event.value === 'object') {
              const taskListId = event.value.taskListId;
              myPatchedInstance?.taskListRemoved(taskListId);
            }
            return;
          case TASK_EVENT_ACTIONS.TASK_LIST_CONVERT_TO_BOARD:
            if (typeof event.value === 'object') {
              myPatchedInstance?.convertToBoard(event.value.boardId, event.value.taskListId);
            }
        }
      }
    });
  };

  get featWorkspaceTasksInputs() {
    return {
      workspaceContext: {
        workspaceId: this.workspaceId,
      },
      nimDetailedViewFloating: true,
      nimAllowedActions: this.tasksAllowedActions ?? {
        ...TasksManager.AllTaskActionsAllowed(this.isWorkspaceMember),
        membersInvite: this.canInvite,
        ...(this.editorInstanceGetter()?.mode === 'readonly'
          ? {
              taskListDelete: false,
              taskListConvertToBoard: false,
              boardDelete: false,
              taskCopyLink: false,
              taskConvertToTaskList: false,
              workspaceLabelDelete: false,
            }
          : {}),
        ...(this.applyMobileStyles ? { taskDragNDrop: false } : {}),
      },
      nimHideComments: !this.isWorkspaceMember,
      nimCommentsDisabled: !this.isWorkspaceMember,
      nimHideAttachments: !this.isWorkspaceMember,
      nimMobileView: this.applyMobileStyles,
      nimTheme: 'dark' === this.theme ? 'cyan-cyan-dark' : 'cyan-cyan-light',
      theme: 'dark' === this.theme ? 'cyan-cyan-dark' : 'cyan-cyan-light',
    };
  }

  public featWorkspaceTasksOutputs = {};

  createTasksList = async (): Promise<string> => {
    const uniqueId = randomString(16);
    const title = this.t ? this.t('editor.editor-tasks.default_task_list_name') : 'Task List';

    const taskListDraft = { title, globalId: uniqueId, noteGlobalId: this.noteId, portalId: this.portalId };
    const newTaskList = await this.TasksApiProxy.apiCreateTaskList(this.workspaceId, taskListDraft);
    // At this point, we don't have the data to run the setup, so an interval is used until it is set up.
    const interval = setInterval(() => {
      if (TasksManager.taskInitedWorkspaceId !== this.workspaceId) {
        const createTaskEvent = new CustomEvent('ng2:tasks:create-task', {
          detail: { taskListId: uniqueId, workspaceId: this.workspaceId },
        });
        document.dispatchEvent(createTaskEvent);
        clearInterval(interval);
      }
    }, 100);
    this.log('created new task list', newTaskList);

    return uniqueId;
  };

  duplicateTasksList = (tasksListId: string, editorRoom: string | undefined): Promise<string> => {
    return new Promise((resolve, reject) => {
      const oldWorkspaceId = editorRoom ? editorRoom.split('_')[0] : null;
      this.TasksApiProxy.apiDuplicateTaskList(oldWorkspaceId ?? this.workspaceId, tasksListId, {
        noteGlobalId: this.noteId,
        workspaceId: this.workspaceId,
        updateBlot: false,
      })
        .then((res) => {
          this.log('duplicated task list', res);
          resolve(res.globalId);
        })
        .catch((err) => {
          // try duplicate allowed for all task list
          if (oldWorkspaceId !== null) {
            axiosClient
              .post<{ globalId: string }>(
                `${generalEnv.nextApiPath}/templates/taskLists/${oldWorkspaceId}/${tasksListId}/copy`,
                {
                  targetNoteGlobalId: this.noteId,
                  targetWorkspaceId: this.workspaceId,
                }
              )
              .then((res) => {
                this.log('duplicated task list', res.data);
                resolve(res.data.globalId);
              })
              .catch((_) => {
                reject();
              });
          } else {
            reject();
          }
        });
    });
  };

  taskListRemove = async (taskListId: string) => {
    await this.TasksApiProxy.apiDeleteTaskList(this.workspaceId, taskListId, this.readOnly);
    return taskListId;
  };

  convertToColumn = async (boardId: string, taskListId: string, beforeColumnId: string | null) => {
    console.log('convertToColumn', boardId, taskListId, beforeColumnId);
    await this.TasksApiProxy.apiCreateTaskBoardColumn({
      from: taskListId,
      boardId,
      before: beforeColumnId || undefined,
    });
  };

  private selectPluginStateSlice = (state: unknown) => ({
    // sharedDynamicInputs: getNewStackSharedDynamicInputs(state),
  });

  private getFeatureLevelInputs = () => ({
    workspaceContext: {
      workspaceId: this.workspaceId,
    },
    nimAllowedActions:
      this.tasksAllowedActions ??
      (this.readOnly
        ? TasksManager.AllTaskActionsAllowed(false)
        : { ...TasksManager.AllTaskActionsAllowed(true), membersInvite: this.canInvite }),
    staticAssetPrefix: `${this.boxUrl === '/' ? '' : this.boxUrl}/file/`,
    nimDetailedViewFloating: true,
    nimIsFree: this.isFree,
    nimAnalyticsLabel: TASKS_ANALYTICS_LABEL.TaskList,
    nimShowHeader: false,
  });

  private getFeatureLevelOutputs = () => ({});

  showLimitPopup(reason: string) {
    // this.$ngRedux.dispatch(
    //   this.AppActions.showPremiumInfoPopup({
    //     reason,
    //     pricingReason: 'premium_banner_limit_ws',
    //     title: this.gettextCatalog.getString('Remind your team about deadlines with an advanced plan. '),
    //     orgId: this.orgId,
    //   })
    // );
  }

  private log = (...args: unknown[]) => {
    // tslint:disable-next-line
    console.log('[tasks plugin]', ...args);
  };

  private restoreTaskList = async (taskListId: string) => {
    await this.TasksApiProxy.apiRestoreTaskList(this.workspaceId, this.noteId, taskListId);
    return taskListId;
  };

  static taskInitedWorkspaceId = '';
  static socketInited = false;

  static setupTasks({
    workspaceId,
    noteId,
    orgId,
    userId,
    shareId,
    securityKey,
    sessionId,
    gatewayUrl,
    staticAssetPrefix,
    userRole,
  }: SetupTasksParams) {
    let loadAllEvent: CustomEvent | null = null;
    const isShare = !!(shareId && securityKey);
    const isNote = !!(workspaceId && noteId);

    if (workspaceId !== TasksManager.taskInitedWorkspaceId) {
      const tasksBundle = document.getElementById('tasks-bundle-script');
      if (tasksBundle && tasksBundle.getAttribute('data-loaded') === 'true') {
        const detail = {
          gatewayUrl,
          gatewayToken: sessionId,
          staticAssetPrefix,
          userId,
          userRole: userRole,
          workspaceId,
          orgId,
          skipLoadStartData: isShare,
        };
        const setupApiEvent = new CustomEvent('ng2:tasks:setup-api', { detail });
        document.dispatchEvent(setupApiEvent);
        TasksManager.taskInitedWorkspaceId = workspaceId;
      }
    }

    if (isShare) {
      loadAllEvent = new CustomEvent('ng2:tasks:load-by-share', {
        detail: {
          workspaceId,
          shareId,
          securityKey,
          noteId,
        },
      });
    } else if (isNote) {
      loadAllEvent = new CustomEvent('ng2:tasks:load-by-note-id', {
        detail: {
          workspaceId,
          noteId,
        },
      });
    }

    if (loadAllEvent) {
      document.dispatchEvent(loadAllEvent);
    }

    TasksManager.setupAttachments({ gatewayUrl, sessionId, staticAssetPrefix });
  }

  static setupAttachments({
    gatewayUrl,
    sessionId,
    staticAssetPrefix,
  }: {
    gatewayUrl: string;
    sessionId: string;
    staticAssetPrefix: string;
  }) {
    const setupApiEvent = new CustomEvent('ng2:attachments:setup-api', {
      detail: {
        gatewayUrl,
        gatewayToken: sessionId,
        staticAssetPrefix,
      },
    });
    document.dispatchEvent(setupApiEvent);
  }

  static AllTaskActionsAllowed = (isAllow: boolean) => ({
    boardCreate: isAllow,
    boardUpdate: isAllow,
    boardDelete: isAllow,
    taskListCreate: isAllow,
    taskListUpdate: isAllow,
    taskListDelete: isAllow,
    // taskListConvertToBoard: isAllow,
    taskListConvertToBoard: false,
    taskListNoteNavigate: isAllow,
    taskListNoteUpdate: isAllow,
    taskCreate: isAllow,
    taskUpdate: isAllow,
    taskDelete: isAllow,
    taskDuplicate: isAllow,
    taskCopy: isAllow,
    taskCopyLink: isAllow,
    taskConvertToTaskList: isAllow,
    taskDragNDrop: isAllow,
    taskListDragNDrop: isAllow,
    taskStatusChange: isAllow,
    taskLabelAdd: isAllow,
    taskLabelDelete: isAllow,
    workspaceLabelCreate: isAllow,
    workspaceLabelUpdate: isAllow,
    workspaceLabelDelete: isAllow,
    taskAssigneeCreate: isAllow,
    taskAssigneeDelete: isAllow,
    taskReminderCreate: isAllow,
    taskReminderDelete: isAllow,
    taskCommentAdd: isAllow,
    taskDescriptionEdit: isAllow,
    taskAttachmentAdd: isAllow,
    taskAttachmentDelete: isAllow,
    membersInvite: isAllow,
    calendarDefaultViewChange: isAllow,
  });
}
